import CardCommon from '@op/components/CardCommon';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import { barIcon, lineIcon, tableIcon } from '@op/constants/chart';
import { DBO_TM, DBO_V } from '@op/utils/format';
import React, { Fragment, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import BarAndLine from './BarAndLine';
import './index.scss';
import { useUrlChartInfo } from '../../../hook';
import CommonTable from './Table';
import { downloadAction } from '@op/pages/download/utils';
import BubbleCommon, { TipsMapType } from '@op/components/BubbleCommon';
import moment from 'moment';

export default function ChartItem({
  type = 'views',
  title,
  filter,
  gameInfo,
  isSingleUrl = true,
  setApiLoading,
  tips,
  nearValue,
  timeTips,
}: {
  title: string;
  type?: 'views' | 'comments';
  filter: any;
  gameInfo: any;
  isSingleUrl?: boolean;
  setApiLoading?: (key: string) => void;
  tips: TipsMapType;
  timeTips?: TipsMapType;
  nearValue?: string;
}) {
  const { formatMessage } = useIntl();
  const [chartType, setChartType] = useState<'bar' | 'line' | 'table'>('bar');
  const { loading, totalInfo, chartInfo, isEmpty, tableRecord } =
    useUrlChartInfo({
      filter,
      isSingleUrl,
      singleName: title,
      type,
      setApiLoading,
    });

  const {
    total,
    positiveAndNegative,
    height,
    tableHeight,
    nearValue: realNearValue,
    showTips,
  } = useMemo(() => {
    const { total, positive, negative, commentsTime } = totalInfo;
    const commentTimeInfo = DBO_TM.sFormat(commentsTime);
    const timeText = commentsTime ? `(${commentTimeInfo})` : nearValue;

    const showTips = commentTimeInfo !== DBO_TM.sFormat(filter.end_time);
    if (type === 'views') {
      return {
        total,
        positiveAndNegative: [],
        height: 286,
        tableHeight: 230,
        nearValue: timeText,
        showTips,
      };
    }

    return {
      total,
      positiveAndNegative: [
        {
          text: 'Positive%',
          className: 'success',
          value: DBO_V.percentageValue(positive, total, 4) * 100,
        },
        {
          text: 'Negative%',
          className: 'error',
          value: DBO_V.percentageValue(negative, total, 4) * 100,
        },
      ],
      height: 265,
      tableHeight: 210,
      nearValue: timeText,
      showTips,
    };
  }, [totalInfo, nearValue, filter]);

  return (
    <CardCommon
      className="dbo-url-analysis-card"
      operations={[barIcon, lineIcon, tableIcon]}
      hasDownload
      logBtnId="ChartCard"
      extraInfo={title}
      defaultOperation={chartType}
      title={
        <BubbleCommon placement="top" tipsMap={tips} showIcon={false}>
          {formatMessage({ id: title })}
        </BubbleCommon>
      }
      handleOperationsOnClick={(value: any) => {
        setChartType(value.symbol);
      }}
      handleDownloadOnClick={() => {
        downloadAction({
          key: 'KeywordAndURLAnalysisUrlChart',
          fileName: title,
          filter,
          auxiliaryParams: { dateType: filter.date_type, title },
          data: tableRecord,
        });
      }}
    >
      <>
        <div className="dbo-analysis-value">
          {DBO_V.reviews(total)}
          <BubbleCommon
            placement="top"
            showIcon={showTips && !!timeTips}
            tipsMap={timeTips}
          >
            <span className="near-value-text">{realNearValue}</span>
          </BubbleCommon>
        </div>
        <div className="dbo-url-analysis-card-percentage">
          {positiveAndNegative.map(({ text, className, value }) => {
            return (
              <Fragment key={text}>
                {formatMessage({ id: text })}
                <PercentageItem
                  percentage={value}
                  showIcon={false}
                  formateFunc={DBO_V.ratio}
                  className={className}
                />
              </Fragment>
            );
          })}
        </div>

        {chartType === 'table' ? (
          <CommonTable
            title={title}
            filter={filter}
            maxHeight={tableHeight}
            records={tableRecord}
            isSingle={isSingleUrl}
            isEmpty={isEmpty}
            height={height}
          ></CommonTable>
        ) : (
          <BarAndLine
            loading={loading}
            height={height}
            isEmpty={isEmpty}
            chartType={chartType}
            chartInfo={chartInfo}
            isSingle={isSingleUrl}
            title={title}
          ></BarAndLine>
        )}
      </>
    </CardCommon>
  );
}
